// Web Push: permission is a thing the player asks for, not something the game
// takes (REFOCUS-2026-09.md §4 — "PWA with push for chore windows").
//
// Four rules shape this file:
//
//   1. FAIL CLOSED. No VAPID public key in the build means no push: the support
//      check reports `unconfigured`, the enable button never appears, and
//      `subscribeToPush` refuses. A half-configured deploy must never leave a
//      phone asking for permission it can never use.
//   2. The endpoint is a CAPABILITY. It is a secret URL: whoever holds it can
//      wake the phone. It goes to the server over the authenticated function and
//      is never written to storage the player can read or logged anywhere.
//   3. The server is the authority. A subscription that failed to reach the
//      server is torn down rather than left half-made, so the UI never claims
//      reminders are on when nothing is going to arrive.
//   4. Guest-first still holds. A player with no account gets no push, because
//      the row needs an owner — and the game says so and carries on.

import { registerServiceWorker, activeServiceWorker } from './serviceWorker';
import type { NetResult, SupabaseClient } from './supabase';

export const PUSH_FUNCTION = 'clayfarm-push';

/** Uncompressed P-256 point: 0x04 || X(32) || Y(32). */
const VAPID_KEY_BYTES = 65;

export function base64UrlToBytes(value: string): Uint8Array {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Why a VAPID public key is unusable, or null when it is fine. A key that is the
 * wrong length, wrong curve marker or not base64 is treated exactly like a
 * missing one: `subscribe({ applicationServerKey })` throws on it, and an
 * exception in a permission flow is a phone that now says "blocked" forever.
 */
export function vapidKeyProblem(raw: string | undefined | null): string | null {
  if (!raw) return 'missing';
  const trimmed = raw.trim();
  if (trimmed.length === 0) return 'empty';
  if (!/^[A-Za-z0-9_-]+$/.test(trimmed)) return 'not_base64url';
  let bytes: Uint8Array;
  try {
    bytes = base64UrlToBytes(trimmed);
  } catch {
    return 'not_base64url';
  }
  if (bytes.length !== VAPID_KEY_BYTES) return 'wrong_length';
  if (bytes[0] !== 0x04) return 'not_a_point';
  return null;
}

/** The configured public key, or null — null meaning "do not offer push". */
export function readVapidPublicKey(): string | null {
  const raw = (import.meta.env as Record<string, string | undefined>).VITE_VAPID_PUBLIC_KEY;
  return vapidKeyProblem(raw) === null ? (raw as string).trim() : null;
}

export type PushSupport =
  /** No service worker, no PushManager, or no Notification API. */
  | 'unsupported'
  /** No usable VAPID public key in this build. */
  | 'unconfigured'
  /** The player said no, at the browser. Only they can undo that. */
  | 'denied'
  /** Everything is in place; the game may ask, on its own screen. */
  | 'default'
  /** Permission already granted on this device. */
  | 'granted';

function pushManagerAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    typeof Notification !== 'undefined' &&
    'PushManager' in window
  );
}

export function pushSupport(): PushSupport {
  if (!pushManagerAvailable()) return 'unsupported';
  if (readVapidPublicKey() === null) return 'unconfigured';
  if (Notification.permission === 'denied') return 'denied';
  return Notification.permission === 'granted' ? 'granted' : 'default';
}

/** The slice of `PushSubscription` this module needs, so tests can supply one. */
export interface PushSubscriptionLike {
  endpoint: string;
  expirationTime: number | null;
  getKey(name: 'p256dh' | 'auth'): ArrayBuffer | null;
  unsubscribe(): Promise<boolean>;
}

interface PushManagerLike {
  getSubscription(): Promise<PushSubscriptionLike | null>;
  subscribe(options: { userVisibleOnly: boolean; applicationServerKey: Uint8Array }): Promise<PushSubscriptionLike>;
}

interface RegistrationLike {
  pushManager: PushManagerLike;
}

async function appRegistration(): Promise<RegistrationLike | null> {
  const existing = await activeServiceWorker();
  if (existing) return existing as unknown as RegistrationLike;
  const result = await registerServiceWorker();
  if (result !== 'registered' && result !== 'existing') return null;
  const after = await activeServiceWorker();
  return after ? (after as unknown as RegistrationLike) : null;
}

/** The local subscription, or null. Never throws. */
export async function currentPushSubscription(): Promise<PushSubscriptionLike | null> {
  if (pushSupport() === 'unsupported') return null;
  const registration = await appRegistration();
  if (!registration) return null;
  try {
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

function subscriptionPayload(subscription: PushSubscriptionLike): Record<string, unknown> | null {
  const p256dh = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');
  if (!p256dh || !auth) return null;
  return {
    endpoint: subscription.endpoint,
    expirationTime: subscription.expirationTime ?? null,
    keys: {
      p256dh: bytesToBase64Url(new Uint8Array(p256dh)),
      auth: bytesToBase64Url(new Uint8Array(auth)),
    },
    userAgent: typeof navigator === 'undefined' ? undefined : navigator.userAgent.slice(0, 300),
  };
}

export type PushRefusal =
  | 'unsupported'
  | 'unconfigured'
  | 'denied'
  | 'needs_account'
  | 'not_configured'
  | 'offline';

export type PushOutcome =
  | { status: 'subscribed'; endpoint: string }
  | { status: 'unsubscribed' }
  | { status: 'refused'; reason: PushRefusal }
  | { status: 'failed'; reason: 'no_keys' | 'unavailable' | 'server' | 'offline' | 'not_configured' };

function serverFailure(result: NetResult<unknown>): 'not_configured' | 'offline' | 'server' {
  if (!result.ok && (result.error === 'not_configured' || result.error === 'offline')) return result.error;
  return 'server';
}

/**
 * Asks for permission, subscribes, and registers the endpoint with the server.
 * Only ever called from a tap on the enable button — never on boot, never after
 * a chore (the old in-tab NotificationManager prompt fires on the first chore,
 * which is exactly the nudge the platform's own rules and COPPA both dislike).
 */
export async function subscribeToPush(client: SupabaseClient): Promise<PushOutcome> {
  const support = pushSupport();
  if (support === 'unsupported') return { status: 'refused', reason: 'unsupported' };
  if (support === 'unconfigured') return { status: 'refused', reason: 'unconfigured' };
  if (!client.configured) return { status: 'refused', reason: 'not_configured' };
  if (!client.signedIn) return { status: 'refused', reason: 'needs_account' };

  if (support === 'default') {
    let granted = false;
    try {
      granted = (await Notification.requestPermission()) === 'granted';
    } catch {
      granted = false;
    }
    if (!granted) return { status: 'refused', reason: 'denied' };
  }

  const vapidKey = readVapidPublicKey();
  if (vapidKey === null) return { status: 'refused', reason: 'unconfigured' };

  const registration = await appRegistration();
  if (!registration) return { status: 'failed', reason: 'unavailable' };

  let subscription: PushSubscriptionLike | null = null;
  try {
    // A subscription made against a different VAPID key belongs to a deploy that
    // is gone. Replace it rather than registering an endpoint that can never be
    // signed for.
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      const existingKey = existing.getKey('p256dh');
      if (existingKey && bytesToBase64Url(new Uint8Array(existingKey)) !== vapidKey) {
        await existing.unsubscribe().catch(() => false);
      } else {
        subscription = existing;
      }
    }
    subscription ??= await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToBytes(vapidKey),
    });
  } catch {
    return { status: 'failed', reason: 'unavailable' };
  }

  const payload = subscriptionPayload(subscription);
  if (!payload) {
    await subscription.unsubscribe().catch(() => false);
    return { status: 'failed', reason: 'no_keys' };
  }

  const result = await client.callFunction(PUSH_FUNCTION, {
    action: 'subscribe',
    subscription: payload,
  });
  if (!result.ok) {
    // Fail closed: a local subscription the server has never heard of is a
    // promise the game cannot keep, so it goes away and the player can retry.
    await subscription.unsubscribe().catch(() => false);
    if (result.error === 'unauthorized') return { status: 'refused', reason: 'needs_account' };
    return { status: 'failed', reason: serverFailure(result) };
  }
  return { status: 'subscribed', endpoint: subscription.endpoint };
}

/**
 * Turns reminders off: tells the server first, then the device. If the server
 * leg fails the local subscription is still removed, because a device the player
 * believes is off must actually be off.
 */
export async function unsubscribeFromPush(client: SupabaseClient): Promise<PushOutcome> {
  if (pushSupport() === 'unsupported') return { status: 'refused', reason: 'unsupported' };
  const subscription = await currentPushSubscription();
  if (!subscription) return { status: 'unsubscribed' };

  let serverOk = true;
  if (client.configured && client.signedIn) {
    const result = await client.callFunction(PUSH_FUNCTION, {
      action: 'unsubscribe',
      endpoint: subscription.endpoint,
    });
    if (!result.ok && result.error !== 'unauthorized') serverOk = false;
  }
  await subscription.unsubscribe().catch(() => false);
  return serverOk ? { status: 'unsubscribed' } : { status: 'failed', reason: 'server' };
}

/**
 * Re-registers an endpoint that already exists on the device. Rows disappear
 * when a token rotates, a save is reset or the family switches accounts, and a
 * silent re-registration on boot is how a phone that shows "on" stops being
 * useful. No prompt, no permission request: it only ever re-sends what the
 * player already agreed to.
 */
export async function ensurePushRegistered(client: SupabaseClient): Promise<PushOutcome | null> {
  if (pushSupport() !== 'granted') return null;
  if (!client.configured || !client.signedIn) return null;
  const subscription = await currentPushSubscription();
  if (!subscription) return null;
  const payload = subscriptionPayload(subscription);
  if (!payload) return null;
  const result = await client.callFunction(PUSH_FUNCTION, { action: 'subscribe', subscription: payload });
  return result.ok ? { status: 'subscribed', endpoint: subscription.endpoint } : null;
}

export type PushUiKind =
  | 'unsupported'
  | 'unconfigured'
  | 'denied'
  | 'needs_account'
  | 'off'
  | 'on'
  | 'checking';

export interface PushUiState {
  kind: PushUiKind;
  /** i18n key for the one line of explanation. */
  detailKey: string;
  action: 'enable' | 'disable' | 'none';
}

/**
 * What the reminders screen shows, as a pure function of what is true. Kept out
 * of the screen so the state table is testable without a DOM, and so the copy
 * for every branch is written down in one place instead of scattered through
 * render code.
 */
export function resolvePushUi(input: {
  support: PushSupport;
  signedIn: boolean;
  subscribed: boolean;
  checked: boolean;
}): PushUiState {
  const { support, signedIn, subscribed, checked } = input;
  if (support === 'unsupported') {
    return { kind: 'unsupported', detailKey: 'push.unsupported', action: 'none' };
  }
  if (support === 'unconfigured') {
    return { kind: 'unconfigured', detailKey: 'push.notConfigured', action: 'none' };
  }
  if (support === 'denied') {
    return { kind: 'denied', detailKey: 'push.denied', action: 'none' };
  }
  if (!subscribed && !checked) {
    return { kind: 'checking', detailKey: 'push.checking', action: 'none' };
  }
  if (!signedIn) {
    return { kind: 'needs_account', detailKey: 'push.needsAccount', action: 'none' };
  }
  if (subscribed) {
    return { kind: 'on', detailKey: 'push.on', action: 'disable' };
  }
  return { kind: 'off', detailKey: 'push.off', action: 'enable' };
}
